import React, { Dispatch, SetStateAction } from "react";
import {
  IMessageListRuleTag,
  MessagingListRuleQueryBuilder,
  QueryBuilderGroupTypes,
} from "./MessagingListRuleQueryBuilder";
import styles from "./styles/MessagingListRules.module.scss";
import { Box, Card, Checkbox, Flex, Heading, Text } from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import { useStores } from "../../../stores/useStores";
import {
  IMessageListType,
  IMessagingListModalIntent,
} from "./AddOrEditMessagingListModal";
import {
  MessagingListRecipientDisplay,
  MessagingListRuleDisplay,
} from "./MessagingListRecipientDisplay";

export enum MessagingListQueryOptions {
  Grade = "grade",
  Group = "group",
  Organization = "organization",
  Operator = "operator",
}

export const displayGrade = (tag?: IMessageListRuleTag): boolean => {
  if (tag) {
    return [
      QueryBuilderGroupTypes.GUARDIAN,
      QueryBuilderGroupTypes.STUDENT,
    ].includes(tag.filter as QueryBuilderGroupTypes);
  }
  return false;
};

export interface MessagingListRulesQuery {
  id?: string;
  [MessagingListQueryOptions.Grade]: IMessageListRuleTag | undefined;
  [MessagingListQueryOptions.Group]: IMessageListRuleTag | undefined;
  [MessagingListQueryOptions.Organization]: IMessageListRuleTag | undefined;
  [MessagingListQueryOptions.Operator]: IMessageListRuleTag | undefined;
}

export type IRecipientsType = "individuals" | "groups";

export function MessagingListRules(props: {
  rules: MessagingListRulesQuery[];
  setRules: Dispatch<SetStateAction<MessagingListRulesQuery[]>>;
  selectedRecipients: IUser[];
  setSelectedRecipients: Dispatch<SetStateAction<IUser[]>>;
  messageIntent: IMessageListType;
  modalIntent: IMessagingListModalIntent;
  setMessageListType: Dispatch<SetStateAction<IMessageListType>>;
}) {
  const { userStore } = useStores();

  const [recipientsType, setRecipientsType] =
    React.useState<IRecipientsType | null>(
      props.modalIntent === "createList" ? "groups" : null,
    );

  function addRule(newRule: MessagingListRulesQuery) {
    props.setRules((prevRules) => [...prevRules, newRule]);
  }

  function deleteRule(index: number) {
    props.setRules((prevRules) =>
      prevRules.filter((_, i) => {
        return i !== index;
      }),
    );
  }

  function addRecipients(recipients: IUser[]) {
    props.setSelectedRecipients((value) => {
      const currentRecipientIds = value.map((m) => m.id);
      // don't duplicate recipients
      const recipientsToAdd = recipients.filter(
        (r) => !currentRecipientIds.includes(r.id),
      );
      return [...value, ...recipientsToAdd];
    });
  }

  return (
    <div>
      {recipientsType === null ? (
        <Box py={"1"}>
          <Card>
            <Box pb={"2"}>
              <Heading as={"h3"} size={"3"}>
                Choose an Option
              </Heading>
            </Box>
            <Box display={"inline-block"} mr={"2"}>
              <button
                className={styles.listTypeButton}
                onClick={() => setRecipientsType("individuals")}
              >
                <Box p={"4"}>
                  <Box pb={"2"}>
                    <FontAwesome size={"2x"} name={"fa-user"} prefix={"fas"} />
                  </Box>
                  <Box>
                    <Text weight={"bold"}>Individual(s)</Text>
                  </Box>
                </Box>
              </button>
            </Box>
            <Box display={"inline-block"}>
              <button
                className={styles.listTypeButton}
                onClick={() => setRecipientsType("groups")}
              >
                <Box p={"4"}>
                  <Box pb={"2"}>
                    <FontAwesome size={"2x"} name={"fa-users"} prefix={"fas"} />
                  </Box>
                  <Box>
                    <Text weight={"bold"}>Group(s)</Text>
                  </Box>
                </Box>
              </button>
            </Box>
          </Card>
        </Box>
      ) : (
        <>
          <Box py={"1"}>
            <Card>
              <Box pb={"2"}>
                <Heading as={"h3"} size={"3"}>
                  {recipientsType === "individuals"
                    ? "Filter then Select Individual(s)"
                    : "Select Group(s)"}
                </Heading>
              </Box>
              <MessagingListRuleQueryBuilder
                addRule={addRule}
                setRecipients={addRecipients}
                messageIntent={props.messageIntent}
                modalIntent={props.modalIntent}
                recipientsType={recipientsType}
              />
            </Card>
          </Box>
          {(props.rules.length > 0 || props.selectedRecipients.length > 0) && (
            <Box py={"1"}>
              <Card>
                <Box pb={"2"}>
                  <Heading as={"h3"} size={"3"}>
                    Recipients
                  </Heading>
                </Box>
                {props.rules.map((rule, idx) => {
                  return (
                    <MessagingListRuleDisplay
                      key={idx}
                      rule={rule}
                      deleteRule={() => deleteRule(idx)}
                    />
                  );
                })}
                {props.selectedRecipients.map((recipient) => {
                  return (
                    <MessagingListRecipientDisplay
                      key={recipient.id}
                      recipient={recipient}
                      deleteRecipient={() => {
                        props.setSelectedRecipients((prevRecipients) =>
                          prevRecipients.filter((r) => r.id !== recipient.id),
                        );
                      }}
                    />
                  );
                })}
              </Card>
            </Box>
          )}
        </>
      )}
      {recipientsType === "individuals" &&
        props.modalIntent === "createMessage" && (
          <Box py={"2"}>
            <Text as="label" size="2">
              <Flex gap="2">
                <Checkbox
                  onClick={() => {
                    if (props.messageIntent === "broadcast") {
                      // switching from broadcast to message, remove any rules
                      props.setRules([]);
                    }
                    props.setMessageListType(
                      props.messageIntent === "broadcast"
                        ? "message"
                        : "broadcast",
                    );
                  }}
                  checked={props.messageIntent === "message"}
                />
                <Box>
                  Allow replies.
                  <Text as={"div"} size={"1"} weight={"regular"}>
                    When checked, recipients will be able to reply all. When
                    unchecked, recipients will not be able to reply to anyone.
                  </Text>
                </Box>
              </Flex>
            </Text>
          </Box>
        )}
      {recipientsType && (
        <Box py={"2"}>
          <Text as="label" size="2">
            <Flex gap="2">
              <Checkbox
                onClick={() => {
                  if (
                    props.selectedRecipients.findIndex(
                      (r) => r.id === userStore.id,
                    ) > -1
                  ) {
                    props.setSelectedRecipients((prevRecipients) =>
                      prevRecipients.filter((r) => r.id !== userStore.id),
                    );
                  } else {
                    props.setSelectedRecipients((prevRecipients) => [
                      ...prevRecipients,
                      {
                        fname: userStore.firstName,
                        lname: userStore.lastName,
                        id: userStore.id,
                      } as IUser,
                    ]);
                  }
                }}
                checked={
                  props.selectedRecipients.findIndex(
                    (r) => r.id === userStore.id,
                  ) > -1
                }
              />
              Send me a copy!
            </Flex>
          </Text>
        </Box>
      )}
    </div>
  );
}
